import React, {useState, useEffect, useCallback} from 'react';
import styled from 'styled-components';
import {useHistory} from 'react-router-dom';
import {debounce} from 'lodash';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import SearchRoundedIcon from '@material-ui/icons/SearchRounded';
import IconButton from '@material-ui/core/IconButton';
import ClearRoundedIcon from '@material-ui/icons/ClearRounded';
import CircularProgress from '@material-ui/core/CircularProgress';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import SiteIdSearchSuggestions from './components/SiteIdSearchSuggestions';
import searchAndSort from './functions/searchAndSort';
import SiteTypeSelection from './components/SiteTypeSelection';
import SiteTypeOption from './interfaces/SiteTypeOption';
import {useSiteState} from 'hooks/useSiteInfo/SiteStateProvider';
import {Site} from 'hooks/useSiteInfo/interfaces/Site';
import SiteType from 'hooks/useSiteInfo/interfaces/SiteType';
import BusinessStatus from 'hooks/useSiteInfo/interfaces/BusinessStatus';

//#region styled-components
const SiteIdSearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  &.left {
    align-items: flex-start;
    .site-type-selection {
      margin-left: 4px;
    }
  }

  &.right {
    align-items: flex-end;
  }
`;

const StyledTextFieldContainer = styled.div`
  height: 32px;
  position: relative;
  z-index: 3;
`;

const StyledTextField = styled(TextField)`
  .MuiFilledInput-root {
    background-color: transparent;
    color: ${props => props.theme.text};
    border-radius: 4px;
    border: 1px solid ${props => props.theme.text};
  }
  &.search-active .MuiFilledInput-root {
    border-bottom-left-radius: 0px;
    border-bottom-right-radius: 0px;
  }
  .MuiInputAdornment-filled.MuiInputAdornment-positionStart:not(.MuiInputAdornment-hiddenLabel) {
    margin-top: 1px;
  }
  .MuiFilledInput-input {
    padding: 8px 10px 8px;
    font-size: 0.8rem;
    width: 272px;
  }
  .MuiFilledInput-underline:after,
  .MuiFilledInput-underline:before {
    border-bottom: none;
  }
`;

const StyledInfoText = styled.p`
  font-family: 'Text Regular';
  font-size: 10px;
  margin: 0px;
  color: ${props => props.theme.grey};
  width: 382px;
`;

const StyledIconButton = styled(IconButton)`
  padding: 0px 0px 0px;
  width: 24px;
  color: ${props => props.theme.text};
  &:hover {
    color: ${props => props.theme.subtleText};
  }
`;

const StyledSearchIcon = styled(SearchRoundedIcon)`
  color: ${props => props.theme.text};
`;

const StyledClearRoundedIcon = styled(ClearRoundedIcon)`
  /* color: ${props => props.theme.text}; */
`;

const StyledCircleLoading = styled(CircularProgress)`
  height: 24px;
  width: 24px;
  svg {
    color: ${props => props.theme.subtleText};
  }
`;
//#endregion styled-components

export function getSwitchConnectivityParamsPartialUrlFromSearchItem(searchItem: Site): string {
  const siteIdUrl: string = `?siteId=${searchItem.siteId}`;
  const urlParamArray: string[] = [siteIdUrl];
  return urlParamArray.join('&');
}

interface SiteIdSearchProps {
  urlType: string;
  setError: (error: string) => void;
  clickHandler?: (site: Site) => void;
  alignment?: 'left' | 'right';
  brands?: {
    converse: boolean;
    nike: boolean;
  };
  defaultSiteType?: SiteTypeOption;
  siteTypes?: {
    distributionCenter: boolean;
    retail: boolean;
    showroom: boolean;
  };
}

function SiteIDSearch({urlType, setError, clickHandler, alignment = 'right'}: SiteIdSearchProps) {
  //#region context
  const {state: siteState} = useSiteState();
  //#endRegion context

  //#region localState
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [suggestedSearchItems, setSuggestedSearchItems] = useState<Site[]>([]);
  const [filteredSearchValue, setFilteredSearchValue] = useState<string>('');
  const [selectedSiteType, setSelectedSiteType] = useState<SiteTypeOption>(SiteTypeOption.All);
  //#endregion localState

  let history = useHistory();

  //#region handles
  const clearSearch = () => {
    setSearchValue('');
    setSuggestedSearchItems([]);
    setIsOpen(false);
  };

  const debouncedSetFilteredSearchValue = useCallback(
    debounce((searchValue: string) => {
      const filteredSearchValue = searchValue.toLowerCase();
      setFilteredSearchValue(filteredSearchValue);
    }, 150),
    []
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const onKeyUp = (event: React.KeyboardEvent) => {
    const eventKey = event.key;
    const enterKey = 'Enter';
    if (eventKey === enterKey && suggestedSearchItems.length > 0) {
      if (
        suggestedSearchItems[0].siteType === SiteType.Retail &&
        suggestedSearchItems[0].businessStatus === BusinessStatus.Closed
      ) {
        return;
      }
      history.push(
        `/site-search/${urlType}${getSwitchConnectivityParamsPartialUrlFromSearchItem(
          suggestedSearchItems[0]
        )}`
      );
      clearSearch();
    }
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  const handleClickAway = () => {
    setIsOpen(false);
  };
  //#endregion handles

  useEffect(() => {
    debouncedSetFilteredSearchValue(searchValue);
  }, [searchValue, debouncedSetFilteredSearchValue]);

  useEffect(() => {
    if (
      filteredSearchValue.length > 0 &&
      siteState.isSiteStateReady &&
      siteState.sites.length > 0
    ) {
      const filteredSites = searchAndSort({
        filteredSearchValue,
        sites: siteState.sites,
        selectedSiteTypeOption: selectedSiteType,
      });

      setSuggestedSearchItems(filteredSites);
      setIsOpen(true);
    } else {
      clearSearch();
    }
  }, [siteState, filteredSearchValue, debouncedSetFilteredSearchValue, selectedSiteType]);

  useEffect(() => {
    return () => {
      debouncedSetFilteredSearchValue.cancel();
    };
  }, []);

  //#endregion useEffect

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <SiteIdSearchContainer id="siteid-search-container" className={alignment}>
        <StyledTextFieldContainer>
          <>
            <StyledTextField
              disabled={!siteState.isSiteStateReady}
              id="siteid-search"
              name="siteid-search"
              aria-describedby="site-id-search"
              variant="filled"
              onFocus={handleFocus}
              className={isOpen && suggestedSearchItems.length > 0 ? 'search-active' : ''}
              onChange={handleChange}
              placeholder={'Search'}
              autoComplete={'off'}
              value={searchValue}
              onKeyUp={onKeyUp}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {siteState.isSiteStateReady ? (
                      <StyledSearchIcon />
                    ) : (
                      <StyledCircleLoading size={24} />
                    )}
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <StyledIconButton aria-label="clear search field" onClick={clearSearch}>
                      {searchValue.length > 0 ? <StyledClearRoundedIcon /> : ''}
                    </StyledIconButton>
                  </InputAdornment>
                ),
              }}
            />
            {isOpen && (
              <SiteIdSearchSuggestions
                urlType={urlType}
                suggestedSearchItems={suggestedSearchItems}
                clearUserInput={clearSearch}
                setSwitchError={setError}
                clickHandler={clickHandler}
              />
            )}
          </>
        </StyledTextFieldContainer>
        <StyledInfoText>Site Name, Store Number, City, State, Country, etc</StyledInfoText>
        <SiteTypeSelection
          selectedSiteType={selectedSiteType}
          setSelectedSiteType={setSelectedSiteType}
          siteInfoState={siteState}
          className="site-type-selection"
        />
      </SiteIdSearchContainer>
    </ClickAwayListener>
  );
}

export default SiteIDSearch;
